import { adfFromString, fromString } from '@canvas/adf/from-string';
import { getAdfTextContent } from '@canvas/adf/get-adf-text-content';
import { isEmptyJSONString } from '@canvas/adf/is-empty';
import { textDefaultColor } from '@canvas/constants/colors/base';
import { defaultTextAlignment } from '@canvas/constants/elements/text';
import { type TextElementData, textElementType } from '@canvas/element-text';
import {
  isSmartLinkData,
  type SmartLinkElementData,
} from '@canvas/model/elements/smart-link';
import { type ElementClipboardData } from '@canvas/model/ui';
import { smartLinkElementDataFromURL } from '@canvas/smart-link-utils';
import { Vector2 } from '@canvas/vector';

import { type PasteOrDropPayload } from './clipboard-types.ts';
import { handleFormattedText } from './utils.ts';

const toURL = (url: string) => {
  try {
    return new URL(url);
  } catch {
    return undefined;
  }
};

export const tryParseAsSmartLink = (
  plainText: string | undefined,
  onPasteSuccess?: (type: string, elementData?: ElementClipboardData[]) => void,
  isImprovedCopyPasteFlagEnabled?: boolean,
): PasteOrDropPayload | undefined => {
  if (!plainText) {
    return;
  }
  const trimmedText = plainText.trim();

  const lines = trimmedText
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length > 1) {
    const urls: URL[] = [];
    for (const line of lines) {
      const maybeURL = toURL(line);
      const isValidUrl = isImprovedCopyPasteFlagEnabled
        ? Boolean(maybeURL?.host)
        : Boolean(maybeURL);
      if (isValidUrl && maybeURL) {
        urls.push(maybeURL);
      } else {
        break;
      }
    }

    if (urls.length === lines.length && urls.length > 0) {
      const elementData = urls.map(
        (url) => smartLinkElementDataFromURL(url) as SmartLinkElementData,
      );
      onPasteSuccess?.('text/plain', elementData);
      return {
        elementData,
        needsResize: false,
      };
    }
  }

  const maybeURL = toURL(trimmedText);
  const shouldParseAsSmartLink = isImprovedCopyPasteFlagEnabled
    ? Boolean(maybeURL?.host)
    : Boolean(maybeURL);

  if (shouldParseAsSmartLink && maybeURL) {
    const elementData = [smartLinkElementDataFromURL(maybeURL) as SmartLinkElementData];
    onPasteSuccess?.('text/plain', elementData);
    return {
      elementData,
      needsResize: false,
    };
  }
};

export const handlePlainText = (
  plainText: string | undefined,
  onPasteSuccess?: (type: string, elementData?: ElementClipboardData[]) => void,
  isImprovedCopyPasteFlagEnabled = false,
): PasteOrDropPayload | undefined => {
  if (!plainText) {
    return;
  }

  const smartLinkPayload = tryParseAsSmartLink(
    plainText,
    onPasteSuccess,
    isImprovedCopyPasteFlagEnabled,
  );
  if (smartLinkPayload) {
    return smartLinkPayload;
  }

  const elementData = [textElementDataFromPlainText(plainText)];
  onPasteSuccess?.('text/plain', elementData);
  return {
    elementData,
    needsResize: true,
  };
};

export const handleHtmlText = (
  html: string | undefined,
  plainText?: string,
  onPasteSuccess?: (type: string, elementData?: ElementClipboardData[]) => void,
  isImprovedCopyPasteFlagEnabled = false,
): PasteOrDropPayload | undefined => {
  if (!html) {
    return undefined;
  }

  const formattedText = handleFormattedText(html)?.filter(
    (element) => !isEmptyJSONString(element.text),
  );
  const textForSmartLink =
    plainText ??
    formattedText
      ?.map((element) => getAdfTextContent(adfFromString(element.text)))
      .join('\n');
  const plainTextPayload = handlePlainText(
    textForSmartLink,
    undefined,
    isImprovedCopyPasteFlagEnabled,
  );
  if (plainTextPayload?.elementData.every(isSmartLinkData)) {
    onPasteSuccess?.('text/plain', plainTextPayload.elementData);
    return plainTextPayload;
  }

  if (formattedText?.length) {
    onPasteSuccess?.('text/html', formattedText);
    return {
      elementData: formattedText,
      needsResize: true,
      clipboardHtml: html,
    };
  }

  if (plainTextPayload) {
    onPasteSuccess?.('text/plain', plainTextPayload.elementData);
  }
  return plainTextPayload;
};

export const textElementDataFromPlainText = (
  plainText: string,
): Omit<TextElementData, 'id'> => ({
  type: textElementType,
  /**
   * We set this size at 1x1 pixels so that it can be resized later.
   * @see InputResizeContentSystem {file://./../renderer/src/world/systems/input/resize-content-system.ts}
   */
  size: new Vector2(1, 1),
  text: JSON.stringify(fromString(plainText)),
  position: new Vector2(0, 0),
  allowFlexibleWidth: true,
  color: textDefaultColor,
  basisSize: new Vector2(1, 1),
  basisPosition: new Vector2(0, 0),
  alignment: defaultTextAlignment,
});
